import {
  getDefaultRowInstruction,
  getShortRowInstruction,
  getStripDeliveryRows,
  getWerkplanPlateNumbers,
  describeGrainAndRotation,
} from "@plaatplan/core";
import { resolveCoreMessage } from "@plaatplan/i18n";
import { LengthInput } from "../input/LengthInput";
import { useLocale } from "../i18n/useLocale";
import { formatMaterialLabel } from "./materialLabels";

export function ExportAlertSection({
  layout,
  sourcePanel,
  werkplanScope,
}) {
  const { display } = useLocale();
  const dimensionsNote = display.unitSystem === "imperial"
    ? display.t("web.werkplan.alert.dimensionsImperial")
    : display.t("web.werkplan.alert.dimensionsMetric");

  return (
    <section className="export-alert">
      <strong>{display.t("web.werkplan.alert.title")}</strong>
      <span>{dimensionsNote}</span>
      <span>{display.t("web.werkplan.alert.cutColumns")}</span>
      <span className="kerf-callout">
        {display.t("web.werkplan.alert.kerfInCalculation", {
          kerf: display.formatLength(sourcePanel.kerf || 0),
        })}
      </span>
      <span>{resolveCoreMessage(display, describeGrainAndRotation(sourcePanel))}</span>
      {layout.breakdown.length > 1 && (
        <span>{display.t("web.werkplan.alert.multipleThicknesses")}</span>
      )}
      {!werkplanScope.includeRestSheets && (layout.metrics.restSheetCount ?? 0) > 0 && (
        <span className="kerf-warning">
          {display.t("web.werkplan.alert.restExcluded")}
        </span>
      )}
      {!werkplanScope.includeNewSheets && (layout.metrics.newSheetCount ?? 0) > 0 && (
        <span className="kerf-warning">
          {display.t("web.werkplan.alert.newSheetsExcluded")}
        </span>
      )}
      {Number(sourcePanel.kerf || 0) === 0 && (
        <span className="kerf-warning">
          {display.t("web.werkplan.alert.zeroKerfWarning")}
        </span>
      )}
    </section>
  );
}

export function ExportMaterialOverview({ layout, project, werkplanScope }) {
  const { display } = useLocale();

  return (
    <section className="export-section">
      <h2>{display.t("web.werkplan.materialOverview.title")}</h2>
      {layout.breakdown.length === 0 ? (
        <p className="export-note">{display.t("web.werkplan.materialOverview.empty")}</p>
      ) : (
        <div className="export-table-scroll">
        <table className="export-table compact">
          <thead>
            <tr>
              <th>{display.t("web.werkplan.materialOverview.material")}</th>
              <th>{display.t("web.werkplan.materialOverview.sourceSize")}</th>
              {werkplanScope.includeNewSheets && (
                <th>{display.t("web.werkplan.materialOverview.newSheets")}</th>
              )}
              {werkplanScope.includeRestSheets && (
                <th>{display.t("web.werkplan.materialOverview.restStock")}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {layout.breakdown.map((group) => (
              <tr key={group.thickness}>
                <td>{formatMaterialLabel(project.materialName, group.thickness)}</td>
                <td>{display.formatSheet(project.sourcePanel.length, project.sourcePanel.width)}</td>
                {werkplanScope.includeNewSheets && <td>{group.newSheetCount ?? group.sheetCount}</td>}
                {werkplanScope.includeRestSheets && <td>{group.restSheetCount || 0}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}

export function ExportCutListSections({
  variant,
  rowsByThickness,
  materialName,
  layout,
  werkplanScope,
  exportDraft,
  hasAnyOrderOversize,
  getExportRow,
  updateRowOverride,
  resetRowInstruction,
  resetRowOrderSize,
}) {
  const { display } = useLocale();
  const isShop = variant === "shop";

  return Object.entries(rowsByThickness)
    .sort(([a], [b]) => Number(b) - Number(a))
    .map(([thickness, thicknessRows]) => (
      <section className={`export-section${!isShop ? " export-section-prepare-cutlist" : ""}`} key={thickness}>
        <h2>{display.t("web.werkplan.cutList.title", {
          material: formatMaterialLabel(materialName, thickness),
        })}</h2>
        {!isShop && (
          <p className="export-note export-settings-hint">
            {display.t("web.werkplan.cutList.prepareHint")}
          </p>
        )}
        {!isShop && hasAnyOrderOversize && (
          <p className="export-note export-settings-hint">
            {display.t("web.werkplan.cutList.oversizeHint")}
          </p>
        )}
        {isShop && (
          <p className="export-note">
            {display.t("web.werkplan.cutList.shopHint")}
          </p>
        )}
        <div className="export-table-scroll">
        <table className={`export-table cut-list${isShop ? " cut-list-shop" : ""}`}>
          <thead>
            <tr>
              <th>{display.t("web.werkplan.cutList.quantity")}</th>
              <th>{display.t("web.werkplan.cutList.orderLength")}</th>
              <th>{display.t("web.werkplan.cutList.orderWidth")}</th>
              {!isShop && hasAnyOrderOversize && (
                <th className="screen-only">{display.t("web.werkplan.cutList.finalSize")}</th>
              )}
              <th>{display.t("web.werkplan.cutList.description")}</th>
              {!isShop && <th>{display.t("web.werkplan.cutList.sheet")}</th>}
              <th>{display.t("web.werkplan.cutList.edges")}</th>
              <th className="cut-list-instruction-col">
                {isShop
                  ? display.t("web.werkplan.cutList.remark")
                  : display.t("web.werkplan.cutList.instruction")}
              </th>
            </tr>
          </thead>
          <tbody>
            {thicknessRows.map((row) => {
              const exportRow = getExportRow(row);
              const defaultInstruction = resolveCoreMessage(
                display,
                getDefaultRowInstruction(exportRow, exportDraft),
              );
              const instructionValue = exportRow.instructionEdited
                ? exportRow.instruction
                : defaultInstruction;
              const shortInstruction = resolveCoreMessage(
                display,
                getShortRowInstruction(exportRow, exportDraft, exportRow),
              );
              const shopInstruction = instructionValue.trim() || shortInstruction;

              return (
                <tr key={row.id} className={!isShop && exportRow.hasOrderOversize ? "cut-list-row-oversize" : ""}>
                  <td className="number">{row.quantity}</td>
                  <td className="important-size order-size-cell">
                    {isShop ? (
                      display.formatLength(exportRow.orderLength)
                    ) : (
                      <>
                        <span className="print-only">{display.formatLength(exportRow.orderLength)}</span>
                        <LengthInput
                          className="editable-size no-print"
                          valueMm={exportRow.orderLength}
                          minMm={1}
                          onChangeMm={(value) => updateRowOverride(row.id, "orderLength", value)}
                          aria-label={display.t("web.werkplan.cutList.orderLengthFor", { name: row.name })}
                        />
                      </>
                    )}
                  </td>
                  <td className="important-size order-size-cell">
                    {isShop ? (
                      display.formatLength(exportRow.orderWidth)
                    ) : (
                      <>
                        <span className="print-only">{display.formatLength(exportRow.orderWidth)}</span>
                        <LengthInput
                          className="editable-size no-print"
                          valueMm={exportRow.orderWidth}
                          minMm={1}
                          onChangeMm={(value) => updateRowOverride(row.id, "orderWidth", value)}
                          aria-label={display.t("web.werkplan.cutList.orderWidthFor", { name: row.name })}
                        />
                      </>
                    )}
                  </td>
                  {!isShop && hasAnyOrderOversize && (
                    <td className="order-final-size screen-only">
                      {exportRow.hasOrderOversize
                        ? display.formatPanelSize(row.finalLength, row.finalWidth)
                        : "-"}
                    </td>
                  )}
                  <td><strong>{row.name}</strong><small>{row.projectName}</small></td>
                  {!isShop && <td>{getWerkplanPlateNumbers(layout, row.id, werkplanScope) || "-"}</td>}
                  <td>{resolveCoreMessage(display, row.edges)}</td>
                  <td className="instruction-cell">
                    {isShop ? (
                      <span className="shop-instruction-text">{shopInstruction}</span>
                    ) : (
                      <>
                        <span className="print-row-instruction">{shortInstruction}</span>
                        <textarea
                          className={instructionValue.trim() ? "" : "empty-print-field"}
                          value={instructionValue}
                          onChange={(event) => updateRowOverride(row.id, "instruction", event.target.value)}
                          rows={2}
                          aria-label={display.t("web.werkplan.cutList.instructionFor", { name: row.name })}
                        />
                        {exportRow.instructionEdited && (
                          <button
                            type="button"
                            className="reset-instruction-button no-print"
                            onClick={() => resetRowInstruction(row.id)}
                          >
                            {display.t("web.werkplan.cutList.resetInstruction")}
                          </button>
                        )}
                        {exportRow.orderSizeEdited && (
                          <button
                            type="button"
                            className="reset-instruction-button no-print"
                            onClick={() => resetRowOrderSize(row.id)}
                          >
                            {display.t("web.werkplan.cutList.resetOrderSize")}
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </section>
    ));
}

export function ExportStripDeliverySection({ exportSheets, sourcePanel, exportDraft }) {
  const { display } = useLocale();
  const stripRows = getStripDeliveryRows({ sheets: exportSheets }, sourcePanel, exportDraft);
  if (stripRows.length === 0) return null;

  return (
    <section className="export-section strip-delivery-section">
      <h2>{display.t("web.werkplan.stripDelivery.title")}</h2>
      <p className="export-note">
        {display.t("web.werkplan.stripDelivery.note")}
      </p>
      <div className="export-table-scroll">
      <table className="export-table compact">
        <thead>
          <tr>
            <th>{display.t("web.werkplan.stripDelivery.sheet")}</th>
            <th>{display.t("web.werkplan.stripDelivery.thickness")}</th>
            <th>{display.t("web.werkplan.stripDelivery.part")}</th>
            <th>{display.t("web.werkplan.stripDelivery.stripSize")}</th>
            <th>{display.t("web.werkplan.stripDelivery.finalSize")}</th>
            <th>{display.t("web.werkplan.stripDelivery.selfCut")}</th>
          </tr>
        </thead>
        <tbody>
          {stripRows.map((row, index) => (
            <tr key={`${row.sheetNumber}-${row.name}-${index}`}>
              <td>{row.sheetNumber}</td>
              <td>{display.formatThickness(row.thickness)}</td>
              <td>{row.name}</td>
              <td className="important-size">{row.stripSize}</td>
              <td>{row.finalSize}</td>
              <td>{row.selfCut}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </section>
  );
}
